import { useEffect, useState } from 'react';
import { EMAIL, NAV_ROUTES, PHONE, SERVICES_HOME, TRUST_BADGES, WA_LINK } from '../data/siteData';

export function ArrowRight() {
  return (
    <svg className="btn-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function Logo() {
  return (
    <span className="brand">
      <span className="brand-mark" aria-hidden="true">
        <svg viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">

          {/* Dark navy background — matches navbar */}
          <rect width="44" height="44" rx="12" fill="#040d1e"/>
          <rect x="1" y="1" width="42" height="42" rx="11" fill="#071428"/>

          {/* Subtle inner glow ring */}
          <rect x="1" y="1" width="42" height="42" rx="11"
            fill="none" stroke="rgba(26,86,255,0.45)" strokeWidth="1"/>

          {/* Headset arc */}
          <path d="M10 22 Q10 11 22 11 Q34 11 34 22"
            fill="none" stroke="#4a8fff" strokeWidth="3"
            strokeLinecap="round"/>

          {/* Left earpiece */}
          <rect x="7" y="21" width="6" height="10" rx="3" fill="#1a56ff"/>

          {/* Right earpiece */}
          <rect x="31" y="21" width="6" height="10" rx="3" fill="#1a56ff"/>

          {/* Mic boom */}
          <path d="M10 31 Q7 38 12 41"
            fill="none" stroke="#1a56ff" strokeWidth="2.2"
            strokeLinecap="round"/>

          {/* Mic capsule — cyan accent */}
          <circle cx="12" cy="41" r="2.5" fill="#00c8f0"/>

          {/* Chat bubble — cyan accent top right */}
          <rect x="27" y="8" width="13" height="9" rx="3" fill="#00c8f0"/>
          <polygon points="28,17 31,21 34,17" fill="#00c8f0"/>

          {/* Live pulse dot — top of headset arc */}
          <circle cx="22" cy="8" r="2.5" fill="#00c8f0">
            <animate attributeName="opacity"
              values="1;0.3;1" dur="2s" repeatCount="indefinite"/>
            <animate attributeName="r"
              values="2.5;3.5;2.5" dur="2s" repeatCount="indefinite"/>
          </circle>

        </svg>
      </span>
      <span className="brand-text">
        <strong>CLEAR<span>DESK</span></strong>
        <small>SUPPORT LTD</small>
      </span>
    </span>
  );
}

export function Header({ activePath, onNavigate }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (path) => {
    onNavigate(path);
    setMenuOpen(false);
  };

  return (
    <header className={`site-header ${scrolled ? 'scrolled' : ''}`}>
      <button className="brand-btn" onClick={() => go('/')} aria-label="ClearDesk Support home">
        <Logo />
      </button>
      <nav className="nav" aria-label="Primary navigation">
        {NAV_ROUTES.map((route) => (
          <button
            key={route.path}
            className={`nav-link ${activePath === route.path ? 'active' : ''}`}
            onClick={() => go(route.path)}
          >
            {route.label}
          </button>
        ))}
        <button className="nav-cta" onClick={() => go('/contact')}>Get a quote</button>
      </nav>
      <button className={`menu-toggle ${menuOpen ? 'open' : ''}`} onClick={() => setMenuOpen(!menuOpen)} aria-expanded={menuOpen} aria-label="Toggle navigation">
        <span />
        <span />
        <span />
      </button>
      {menuOpen && (
        <nav className={`mobile-nav ${menuOpen ? 'open' : ''}`} aria-label="Mobile navigation">
          {NAV_ROUTES.map((route) => (
            <button key={route.path} className="nav-link" onClick={() => go(route.path)}>{route.label}</button>
          ))}
          <button className="nav-cta" onClick={() => go('/contact')}>Get a quote</button>
        </nav>
      )}
    </header>
  );
}

export function Footer({ onNavigate }) {
  return (
    <footer className="footer">
      <div className="footer-main">
        <div className="footer-brand">
          <button className="brand-btn" onClick={() => onNavigate('/')} aria-label="ClearDesk Support home">
            <Logo />
          </button>
          <p>Professional BPO services that keep your business connected, responsive, and ready for every enquiry.</p>
          <p className="footer-legal">
            ClearDesk Support Ltd<br />
            Company No. 17183450<br />
            Registered in England &amp; Wales<br />
            72 Bourne Avenue, Hayes, UB3 1QW
          </p>
        </div>
        <div>
          <h3>Services</h3>
          {SERVICES_HOME.map((service) => (
            <button className="footer-link" key={service.title} onClick={() => onNavigate('/services')}>{service.title}</button>
          ))}
        </div>
        <div>
          <h3>Company</h3>
          {[...NAV_ROUTES, { path: '/privacy-policy', label: 'Privacy Policy' }].map((route) => (
            <button className="footer-link" key={route.path} onClick={() => onNavigate(route.path)}>{route.label}</button>
          ))}
        </div>
        <div>
          <h3>Contact</h3>
          <a className="footer-link" href={`tel:${PHONE.replace(/\s/g, '')}`}>{PHONE}</a>
          <a className="footer-link" href={`mailto:${EMAIL}`}>{EMAIL}</a>
          <a className="footer-whatsapp" href={WA_LINK} target="_blank" rel="noreferrer">WhatsApp us</a>
        </div>
      </div>
      <div className="footer-bottom">
        <p>Copyright 2026 ClearDesk Support LTD. All rights reserved.</p>
        <button className="footer-link" onClick={() => onNavigate('/privacy-policy')}>Privacy Policy</button>
      </div>
    </footer>
  );
}

export function FloatingActions() {
  return (
    <div className="floating-actions" aria-label="Quick contact actions">
      <a className="floating-btn" href={`tel:${PHONE.replace(/\s/g, '')}`}>📞 Call</a>
      <a className="floating-btn wa" href={WA_LINK} target="_blank" rel="noreferrer">💬 WhatsApp</a>
    </div>
  );
}

export function PageHero({
  eyebrow,
  title,
  text,
  backgroundImage = "/images/page-hero.jpg"
}) {
  return (
    <section
      className="page-hero"
      style={{
        backgroundImage: `linear-gradient(rgba(0,0,0,0.65), rgba(0,0,0,0.65)), url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat"
      }}
    >
      <div className="page-hero-inner">
        <Eyebrow light>{eyebrow}</Eyebrow>
        <h1>{title}</h1>
        <p>{text}</p>
      </div>
    </section>
  );
}

export function SectionHeading({ eyebrow, title, text, light = false }) {
  return (
    <div className={`section-heading ${light ? 'light' : ''}`}>
      <Eyebrow light={light}>{eyebrow}</Eyebrow>
      <h2>{title}</h2>
      {text && <p>{text}</p>}
    </div>
  );
}

export function Eyebrow({ children, light = false }) {
  return <p className={`eyebrow ${light ? 'light' : ''}`}>{children}</p>;
}

export function Feature({ title, text, lightBg = false }) {
  return (
    <article className={`feature ${lightBg ? 'light-bg' : ''}`}>
      <span className="check"><svg viewBox="0 0 13 13" fill="none"><path d="M2 6.5l3 3 6-6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg></span>
      <div>
        <h3>{title}</h3>
        <p>{text}</p>
      </div>
    </article>
  );
}

export function ContactBand({ onNavigate }) {
  return (
    <section className="contact-band full-band section-pad">
      <div>
        <Eyebrow light>Ready when you are</Eyebrow>
        <h2>Give your customers a faster, clearer response.</h2>
        <p>Start with call handling, overflow cover, or a complete support desk.</p>
      </div>
      <button className="btn light" onClick={() => onNavigate('/contact')}>Contact ClearDesk</button>
    </section>
  );
}

export function TrustedSlider() {
  const badges = [...TRUST_BADGES, ...TRUST_BADGES];
  return (
    <section className="trust-slider" aria-label="Trusted business sectors">
      <div className="trust-slider-inner">
        {/* <span className="trust-label">Trusted by modern service teams</span> */}
        <div className="trust-marquee" aria-hidden="true">
          <div className="trust-track">
            {badges.map((name, index) => (
              <span className="trust-badge" key={`${name}-${index}`}>{name}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
